/**
 * Created by Maxim B. on 17/04/20.
 * Copyright © 2020 Curriculum Ltd. All rights reserved.
 */
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ActivatedRoute } from '@angular/router';
import { MediaItem, MediaType } from '../../../../shared/components/media-item/api-media-item';
import { LearningObjective } from '../../api-subject-details';
import { getRestApiProvider, RestApiService } from '../../../../shared/api/rest-api.service';
import {switchMap, switchMapTo, takeUntil} from 'rxjs/operators';
import {of, pipe, Subject} from 'rxjs';
import { fadeAnimation } from '../../../../shared/animations/fade.animation';
import {FileUploadService} from '../../../../shared/components/edit-question-block/file-upload.service';

@Component({
  selector: 'curr-learning-resources',
  templateUrl: './learning-resources.component.html',
  styleUrls: ['./learning-resources.component.scss'],
  providers: [getRestApiProvider<MediaItem>('curriculumLearningObjectiveResources')],
  animations: [fadeAnimation]
})
export class LearningResourcesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() learningObjective: LearningObjective;
  @Output()
  learningObjectiveUpdated = new EventEmitter<boolean>();
  currResourceType = MediaType.video;
  disabledStatus = true;
  isNewPaper = false;
  newMediaItem: MediaItem;
  isLoading = true;
  ddTypeOptions = [
    { label: 'Video', value: MediaType.video },
    { label: 'Image', value: MediaType.image },
    { label: 'Link', value: MediaType.link },
    { label: 'Document', value: MediaType.document }
  ];
  MediaType = MediaType;

  mediaItems: MediaItem[] = [];

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private learningResourceService: RestApiService<MediaItem>,
    private fileUploadService: FileUploadService) {
  }

  ngOnInit(): void {
    this.isNewPaper = this.route.snapshot.parent?.params.subjectId === 'new';

    this.currResourceType = this.route.snapshot.queryParams.resources
      ? this.route.snapshot.queryParams.resources
      : MediaType.video;
    this.initByMediaType(this.currResourceType);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initByMediaType(this.currResourceType);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initByMediaType(mediaType: MediaType) {
    this.currResourceType = mediaType;
    this.mediaItems = [];
    this.isLoading = true;

    this.learningResourceService
      .getAll({
        resource_type: mediaType,
        curriculum_learning_objective_id: this.learningObjective.id
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        async (res: MediaItem[]) => {
          try {
            const updatedItems = await Promise.all(
              res.map(async mediaItem => {
                if (mediaItem.resource_type === MediaType.image && mediaItem.resource) {
                  try {
                    const response = await this.fileUploadService
                      .download(mediaItem.resource, 'IMAGE')
                      .toPromise();

                    // @ts-ignore
                    const bytes = response?.data?.bytes;
                    if (bytes) {
                      mediaItem.previewUrl = 'data:image/png;base64,' + bytes;
                    }
                  } catch (error) {
                    console.error('Error loading image:', error);
                  }
                }

                if (mediaItem.resource_type === MediaType.document && mediaItem.resource) {
                  try {
                    const response = await this.fileUploadService
                      .download(mediaItem.resource, 'DOC')
                      .toPromise();

                    // @ts-ignore
                    const bytes = response?.data?.bytes as string | undefined;
                    // @ts-ignore
                    const contentType = response?.data?.contentType as string | undefined;

                    if (bytes) {
                      const mime = this.getMimeByExtension(mediaItem.resource, contentType);
                      const dataUrl = `data:${mime};base64,${bytes}`;

                      mediaItem.docUrl = dataUrl;
                    }
                  } catch (error) {
                    console.error('Error loading file:', error);
                  }
                }

                return mediaItem;
              })
            );

            this.mediaItems = updatedItems;
          } catch (error) {
            console.error('Error processing resources:', error);
          } finally {
            this.isLoading = false;
          }
        },
        (error) => {
          console.error('Error fetching resources:', error);
          this.isLoading = false;
        }
      );
  }

  private getMimeByExtension(fileName?: string, fallback?: string) {
    const ext = (fileName || '').split('.').pop()?.toLowerCase();
    if (fallback) return fallback;
    switch (ext) {
      case 'pdf':  return 'application/pdf';
      case 'doc':  return 'application/msword';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'ppt':  return 'application/vnd.ms-powerpoint';
      case 'pptx': return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'xls':  return 'application/vnd.ms-excel';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      default:     return 'application/octet-stream';
    }
  }

  dropItem(event: CdkDragDrop<string[]>, type) {
    moveItemInArray(this.mediaItems, event.previousIndex, event.currentIndex);
  }

  addMedia() {
    this.newMediaItem = {
      title: '',
      estimated_time: 1,
      description: '',
      resource: '',
      resource_type: this.currResourceType,
      curriculum_lobj_id: this.learningObjective.id
    };
  }

  createNewMediaItem(item: MediaItem) {
    this.isLoading = true;
    this.learningResourceService
      .add(item)
      // .pipe(this.updateFile(item) as any)
      .subscribe((res: MediaItem) => {
        if (res.resource_type === MediaType.document || res.resource_type === MediaType.image) {
          this.mediaItems.push(res);
        } else {
          this.mediaItems.push({ ...item, id: res.id });
        }
        this.learningObjectiveUpdated.emit(true);
      });
    this.newMediaItem = undefined;
  }

  updateMediaItem(item: MediaItem, index?: number) {
    this.isLoading = true;
    this.learningResourceService
      .update({ ...item, curriculum_learning_objective_id: item.curriculum_lobj_id } as any, item.id)
      // .pipe(this.updateFile(item) as any)
      .pipe(
        switchMapTo(
          this.learningResourceService.getAll({
            resource_type: this.currResourceType,
            curriculum_learning_objective_id: this.learningObjective.id
          })
        )
      ).subscribe((res: MediaItem[]) => {
        this.mediaItems = res;
        this.learningObjectiveUpdated.emit(true);
      });
    this.newMediaItem = undefined;
  }

  deleteMediaItem(item: MediaItem) {
    this.isLoading = true;
    this.learningResourceService.remove(item.id).subscribe(res => {
      this.learningObjectiveUpdated.emit(true);
      this.mediaItems.splice(
        this.mediaItems.findIndex(mediaItem => mediaItem.id === item.id),
        1
      );
    });
  }

  cancelAddMedia() {
    this.newMediaItem = undefined;
  }

  setType(val: string) {
    this.currResourceType = val as MediaType;
  }

  onSaveClick() {
    this.disabledStatus = true;
    this.newMediaItem = undefined;
  }

  // TODO: check if needed?
  // tslint:disable-next-line:no-unused-variable
  private updateFile = item =>
    pipe(
      switchMap((res: any) => {
        if ((item.resource_type === MediaType.image || item.resource_type === MediaType.document) && item.file) {
          return this.learningResourceService.patchFile({ resource_type: item.resource_type, file: item.file }, res.id);
        } else {
          return of(res);
        }
      })
    );
}
